import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Users, FileText, DollarSign, Mail, BarChart3, Tag,
  Calendar, Clock, ChevronDown, ChevronRight, BookOpen, UserCog,
  LogIn, LogOut, Bell, Download, Send, Search, Plus, Pencil, ArrowRight
} from 'lucide-react';

const sections = [
  {
    id: 'overview',
    icon: LayoutDashboard,
    title: 'CRM Overview',
    color: '#FF3B6B',
    content: [
      { heading: 'What it shows', text: 'Your business KPIs at a glance: net revenue, total customers, total bookings, retention rate, today\'s parties, upcoming week, average spend per customer, and total refunds.' },
      { heading: 'Revenue by Package', text: 'Visual breakdown of revenue generated by each party package (Create, Celebrate, Signature Edible).' },
      { heading: 'Monthly Revenue Trend', text: 'Bar chart showing revenue progression month over month.' },
      { heading: 'Integration Status', text: 'Shows which services are connected (Stripe, SendGrid, Twilio). Green = Connected, Gray = Mocked. Add API keys in backend .env to enable live services.' }
    ]
  },
  {
    id: 'customers',
    icon: Users,
    title: 'Customer Management',
    color: '#06D6A0',
    content: [
      { heading: 'Customer List', text: 'All customers aggregated from bookings. Search by name, email, or phone. Sort by recent activity, top spenders, or most bookings.' },
      { heading: 'Customer Profile', text: 'Click the eye icon to see a full profile: contact info, tags, internal notes, complete booking history, payment history, and communication log.' },
      { heading: 'Tags', text: 'Add tags like "VIP", "Repeat", "Referred" to segment customers. Tags help you target discounts and bulk messages to specific groups.' },
      { heading: 'Notes', text: 'Add internal notes about customer preferences, special requests, or important information. Only visible to admin.' }
    ]
  },
  {
    id: 'employees',
    icon: UserCog,
    title: 'Employee Management',
    color: '#6366F1',
    content: [
      { heading: 'Adding Employees', text: 'Click "Add Employee" to create accounts. Set name, email, role (Party Host, Manager, Staff), hourly rate, and password. Employees use the same login page.' },
      { heading: 'Employee Roles', text: 'Party Host - manages party activities. Manager - supervises operations. Staff - general tasks. Role determines badge color in the system.' },
      { heading: 'Time Tracking', text: 'Employees clock in/out from their Employee Portal (/employee). All entries show in the "Time Entries" tab with dates, times, and hours worked.' },
      { heading: 'Payroll Summary', text: 'The "Payroll" tab shows weekly hours and gross pay per employee based on their hourly rate. Use this for payroll processing.' },
      { heading: 'Editing & Deactivating', text: 'Edit employee details, change roles, update hourly rates, or deactivate accounts without deleting history.' }
    ]
  },
  {
    id: 'invoices',
    icon: FileText,
    title: 'Invoices',
    color: '#FFD166',
    content: [
      { heading: 'Generating Invoices', text: 'Click "Generate Invoice" and select a booking. An invoice is automatically created with all pricing details, tax, and payment status.' },
      { heading: 'Invoice Status', text: 'Unpaid = no payment received. Partial = deposit paid, balance pending. Paid = fully paid.' },
      { heading: 'PDF Download', text: 'Click the download icon to get a professional branded PDF invoice. Customers can also download their invoices from "My Bookings".' },
      { heading: 'Customer Access', text: 'Customers see their invoices under My Bookings > Invoices tab. They can view details, download PDFs, and pay outstanding balances directly.' }
    ]
  },
  {
    id: 'payments',
    icon: DollarSign,
    title: 'Payments & Refunds',
    color: '#06D6A0',
    content: [
      { heading: 'Payment History', text: 'View all transactions with method (Stripe/PayPal), type (deposit/balance), amount, status, and timestamp.' },
      { heading: 'Processing Refunds', text: 'Click "Process Refund", enter the booking ID, refund amount, and reason. A refund record is created and a notification email is logged.' },
      { heading: 'Payment Status', text: 'Paid = successful payment. Pending = initiated but not completed. Failed = payment attempt failed.' }
    ]
  },
  {
    id: 'communications',
    icon: Mail,
    title: 'Communications',
    color: '#FF3B6B',
    content: [
      { heading: 'Sending Messages', text: 'Click "Send Message" to send an email or SMS to a specific customer. Choose type, enter recipient, subject, and body.' },
      { heading: 'Bulk Messaging', text: '"Bulk Message" sends to ALL customers in your database. Great for promotions, announcements, or seasonal offers.' },
      { heading: 'Email Templates', text: '5 pre-built templates (Welcome, Balance Reminder, Thank You, Party Reminder, Re-booking Offer). Use template variables like {customer_name}, {child_name}, {party_date}.' },
      { heading: 'Editing Templates', text: 'Click the pencil icon on any template to customize subject and body. Click "Use" to pre-fill a new message with that template.' },
      { heading: 'Communication Log', text: 'Complete history of all messages sent. Shows type (email/SMS), recipient, subject, status, and whether it was bulk or auto-generated.' },
      { heading: 'Live vs Mocked', text: 'When SendGrid/Twilio API keys are configured, messages are sent for real. Without keys, they\'re logged to the database as "mocked".' }
    ]
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Analytics Dashboard',
    color: '#6366F1',
    content: [
      { heading: 'Revenue Charts', text: 'Monthly revenue bar chart, revenue by package breakdown, net revenue after refunds.' },
      { heading: 'Booking Trends', text: 'Monthly booking line chart, status distribution, popular time slots, and average guest count.' },
      { heading: 'Customer Insights', text: 'New customer acquisition by month, retention rate (repeat customers / total), and average spend per customer.' },
      { heading: 'Using Data', text: 'Use analytics to identify peak months, most popular packages, and customer segments for targeted marketing.' }
    ]
  },
  {
    id: 'discounts',
    icon: Tag,
    title: 'Discounts & Reminders',
    color: '#FFD166',
    content: [
      { heading: 'Creating Discounts', text: 'Click "New Discount". Set code (or auto-generate), type (percentage or fixed amount), value, max uses, and expiry date.' },
      { heading: 'Auto Re-booking Reminders', text: 'Enable "Auto Reminder" on a discount. Set days after last party (e.g., 30). When you click "Send Auto Reminders", eligible past customers receive the discount code.' },
      { heading: 'Managing Discounts', text: 'Edit codes, toggle active/inactive, update values, or delete. Track usage count vs max uses.' },
      { heading: 'Strategy Tips', text: 'Create a 10% "COMEBACK" code with 30-day auto-reminder for repeat business. Use time-limited codes for seasonal promotions.' }
    ]
  },
  {
    id: 'employee-portal',
    icon: Clock,
    title: 'Employee Portal Guide',
    color: '#06D6A0',
    content: [
      { heading: 'Logging In', text: 'Employees use the same login page (/login) with their email and password set by admin. They\'re automatically redirected to the Employee Portal.' },
      { heading: 'Clocking In', text: 'Press the green "Clock In" button to start your shift. A timer shows elapsed time.' },
      { heading: 'Clocking Out', text: 'Press "Clock Out" to end your shift. Your hours are automatically calculated and logged.' },
      { heading: 'Viewing Time Logs', text: 'See all your recent shifts with dates, clock in/out times, and hours. Weekly summaries show total hours per week.' },
      { heading: 'Important Notes', text: 'Always clock out at end of shift. If you forget, ask a manager to adjust. Your hours are used for payroll calculation.' }
    ]
  }
];

const SectionCard = ({ section, isOpen, onToggle }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden mb-4"
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-4 p-6 text-left hover:bg-[#FFFDF8] transition-colors"
      data-testid={`guide-section-${section.id}`}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${section.color}15` }}>
        <section.icon className="w-5 h-5" style={{ color: section.color }} />
      </div>
      <h3 className="flex-1 text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        {section.title}
      </h3>
      {isOpen ? <ChevronDown className="w-5 h-5 text-[#6B6B7A]" /> : <ChevronRight className="w-5 h-5 text-[#6B6B7A]" />}
    </button>

    {isOpen && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        className="px-6 pb-6"
      >
        <div className="space-y-4 ml-14">
          {section.content.map((item, i) => (
            <div key={i}>
              <h4 className="text-sm font-bold text-[#1E1E24] mb-1">{item.heading}</h4>
              <p className="text-sm text-[#6B6B7A] leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </motion.div>
    )}
  </motion.div>
);

export const CrmGuide = () => {
  const [openSections, setOpenSections] = useState(['overview']);

  const toggleSection = (id) => {
    setOpenSections(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const expandAll = () => setOpenSections(sections.map(s => s.id));
  const collapseAll = () => setOpenSections([]);

  return (
    <div>
      <div className="flex justify-between items-start mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#FF3B6B]/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#FF3B6B]" />
            </div>
            <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              CRM User Guide
            </h1>
          </div>
          <p className="text-[#6B6B7A] ml-[52px]">Complete training guide for all CRM features</p>
        </div>
        <div className="flex gap-2">
          <button onClick={expandAll} className="text-sm text-[#FF3B6B] hover:underline" data-testid="expand-all">
            Expand All
          </button>
          <span className="text-[#E8E5DF]">|</span>
          <button onClick={collapseAll} className="text-sm text-[#6B6B7A] hover:underline" data-testid="collapse-all">
            Collapse All
          </button>
        </div>
      </div>

      {/* Quick Start */}
      <div className="bg-gradient-to-r from-[#FF3B6B] to-[#E62E58] rounded-2xl p-6 mb-8 text-white">
        <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>Quick Start</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { step: '1', text: 'Overview - Check daily KPIs and integration status' },
            { step: '2', text: 'Employees - Add staff and set hourly rates' },
            { step: '3', text: 'Customers - Review bookings and tag VIP customers' },
            { step: '4', text: 'Discounts - Create re-booking offers with auto-reminders' }
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 font-bold text-sm">
                {item.step}
              </div>
              <p className="text-sm text-white/90">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sections */}
      {sections.map(section => (
        <SectionCard
          key={section.id}
          section={section}
          isOpen={openSections.includes(section.id)}
          onToggle={() => toggleSection(section.id)}
        />
      ))}

      {/* FAQ */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6 mt-8">
        <h3 className="text-lg font-semibold text-[#1E1E24] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Frequently Asked Questions
        </h3>
        <div className="space-y-4">
          {[
            { q: 'How do I enable real email sending?', a: 'Add your SendGrid API key to /app/backend/.env as SENDGRID_API_KEY and set SENDGRID_SENDER_EMAIL to your verified sender. Restart the backend.' },
            { q: 'How do I enable SMS?', a: 'Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_PHONE_NUMBER to /app/backend/.env. Restart the backend.' },
            { q: 'Can employees see other employees\' time entries?', a: 'No. Employees only see their own clock-in/out history. Only admins can see all employee time entries and payroll.' },
            { q: 'How do customers access their invoices?', a: 'Customers log in and go to "My Bookings". There\'s an "Invoices" tab showing all their invoices with download and pay options.' },
            { q: 'What happens if an employee forgets to clock out?', a: 'An admin can see the active (no clock-out) entries in the Time Entries tab. Currently, manual adjustments need to be done in the database.' },
            { q: 'How do auto-reminders work?', a: 'Create a discount with "Auto Reminder" enabled and set the number of days after the last party. Click "Send Auto Reminders" to process - it sends the discount code to eligible past customers.' }
          ].map((faq, i) => (
            <div key={i} className="p-4 bg-[#FFFDF8] rounded-xl">
              <p className="text-sm font-semibold text-[#1E1E24] mb-1">{faq.q}</p>
              <p className="text-sm text-[#6B6B7A]">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CrmGuide;
